import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Avatar,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Collapse
} from '@mui/material';
import {
  Send as SendIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  Comment as CommentIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Public as PublicIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import {
  fetchPosts,
  createPost,
  deletePost,
  toggleLike,
  fetchComments,
  addComment,
  deleteComment
} from '../services/feedService';
import { getServerUrl } from '../utils/apiUrl';

const MobileFeed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostVisibility, setNewPostVisibility] = useState('public');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});

  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const canSeeRestrictedPosts = currentUser?.isAdmin || currentUser?.canCreateWorkOrder;

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchPosts(token);
      setPosts(data);
    } catch (err) {
      console.error('Kunne ikke laste innlegg:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Bildet er for stort. Maksimal størrelse er 5MB.');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPostContent.trim()) return;

    try {
      await createPost(token, newPostContent, newPostVisibility, imageFile);
      setNewPostContent('');
      setNewPostVisibility('public');
      setImageFile(null);
      setImagePreview(null);
      await loadPosts();
    } catch (err) {
      alert('Kunne ikke opprette innlegg: ' + err.message);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette innlegget?')) return;
    try {
      await deletePost(token, postId);
      await loadPosts();
    } catch (err) {
      alert('Kunne ikke slette innlegg: ' + err.message);
    }
  };

  const handleToggleLike = async (postId) => {
    try {
      await toggleLike(token, postId);
      await loadPosts();
    } catch (err) {
      console.error('Kunne ikke like innlegg:', err);
    }
  };

  const handleToggleComments = async (postId) => {
    const isExpanded = expandedComments[postId];
    
    if (!isExpanded && !comments[postId]) {
      try {
        const postComments = await fetchComments(token, postId);
        setComments(prev => ({ ...prev, [postId]: postComments }));
      } catch (err) {
        console.error('Kunne ikke laste kommentarer:', err);
      }
    }
    
    setExpandedComments(prev => ({ ...prev, [postId]: !isExpanded }));
  };

  const handleAddComment = async (postId) => {
    const content = newComment[postId]?.trim();
    if (!content) return;

    try {
      await addComment(token, postId, content);
      const updatedComments = await fetchComments(token, postId);
      setComments(prev => ({ ...prev, [postId]: updatedComments }));
      setNewComment(prev => ({ ...prev, [postId]: '' }));
      await loadPosts();
    } catch (err) {
      alert('Kunne ikke legge til kommentar: ' + err.message);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne kommentaren?')) return;
    try {
      await deleteComment(token, commentId);
      const updatedComments = await fetchComments(token, postId);
      setComments(prev => ({ ...prev, [postId]: updatedComments }));
      await loadPosts();
    } catch (err) {
      alert('Kunne ikke slette kommentar: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Nå nettopp';
    if (diffMins < 60) return `${diffMins}m siden`;
    if (diffHours < 24) return `${diffHours}t siden`;
    if (diffDays < 7) return `${diffDays}d siden`;
    return date.toLocaleDateString('nb-NO');
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
      {/* Create Post */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Nytt innlegg
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Hva vil du dele?"
            value={newPostContent}
            onChange={(e) => setNewPostContent(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          {imagePreview && (
            <Box sx={{ mb: 2, position: 'relative' }}>
              <img 
                src={imagePreview} 
                alt="Preview" 
                style={{ width: '100%', maxHeight: 300, objectFit: 'contain' }}
              />
              <IconButton
                onClick={() => {
                  setImageFile(null);
                  setImagePreview(null);
                }}
                sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)' }}
              >
                <DeleteIcon sx={{ color: 'white' }} />
              </IconButton>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Synlighet</InputLabel>
              <Select
                value={newPostVisibility}
                label="Synlighet"
                onChange={(e) => setNewPostVisibility(e.target.value)}
              >
                <MenuItem value="public">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublicIcon fontSize="small" />
                    Alle
                  </Box>
                </MenuItem>
                {canSeeRestrictedPosts && (
                  <MenuItem value="restricted">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockIcon fontSize="small" />
                      Ledelse
                    </Box>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={handleImageSelect}
            />
            <label htmlFor="image-upload">
              <IconButton component="span" color="primary">
                <ImageIcon />
              </IconButton>
            </label>

            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={handleCreatePost}
              disabled={!newPostContent.trim()}
              sx={{ ml: 'auto' }}
            >
              Del
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Posts Feed */}
      {loading && posts.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        posts.map((post) => (
          <Card key={post.id} sx={{ mb: 2 }}>
            <CardContent>
              {/* Post Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                  {post.author_name?.charAt(0) || 'U'}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {post.author_name || 'Ukjent bruker'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(post.created_at)}
                    </Typography>
                    <Chip
                      size="small"
                      icon={post.visibility === 'public' ? <PublicIcon /> : <LockIcon />}
                      label={post.visibility === 'public' ? 'Alle' : 'Ledelse'}
                      sx={{ height: 20 }}
                    />
                  </Box>
                </Box>
                {post.user_id === currentUser?.id && (
                  <IconButton onClick={() => handleDeletePost(post.id)} size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* Post Content */}
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {post.content}
              </Typography>

              {/* Post Image */}
              {post.image_url && (
                <Box sx={{ mb: 2 }}>
                  <img
                    src={`${getServerUrl()}${post.image_url}?token=${encodeURIComponent(token)}`}
                    alt="Post"
                    style={{ width: '100%', maxHeight: 400, objectFit: 'contain' }}
                  />
                </Box>
              )}

              {/* Post Actions */}
              <Box sx={{ display: 'flex', gap: 2, borderTop: '1px solid #eee', pt: 1 }}>
                <Button
                  size="small"
                  startIcon={post.user_liked > 0 ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                  onClick={() => handleToggleLike(post.id)}
                  color={post.user_liked > 0 ? 'primary' : 'inherit'}
                >
                  {post.like_count || 0}
                </Button>
                <Button
                  size="small"
                  startIcon={<CommentIcon />}
                  onClick={() => handleToggleComments(post.id)}
                >
                  {post.comment_count || 0}
                </Button>
              </Box>

              {/* Comments Section */}
              <Collapse in={expandedComments[post.id]}>
                <Box sx={{ mt: 2, borderTop: '1px solid #eee', pt: 2 }}>
                  {/* Existing Comments */}
                  {comments[post.id]?.map((comment) => (
                    <Box key={comment.id} sx={{ mb: 2, display: 'flex', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                        {comment.author_name?.charAt(0) || 'U'}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ bgcolor: '#f0f2f5', borderRadius: 2, p: 1 }}>
                          <Typography variant="caption" fontWeight="bold">
                            {comment.author_name}
                          </Typography>
                          <Typography variant="body2">
                            {comment.content}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {formatDate(comment.created_at)}
                        </Typography>
                      </Box>
                      {comment.user_id === currentUser?.id && (
                        <IconButton 
                          onClick={() => handleDeleteComment(post.id, comment.id)} 
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  {/* Add Comment */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      {currentUser?.name?.charAt(0) || 'U'}
                    </Avatar>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Skriv en kommentar..."
                      value={newComment[post.id] || ''}
                      onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAddComment(post.id);
                        }
                      }}
                    />
                    <IconButton
                      color="primary"
                      onClick={() => handleAddComment(post.id)}
                      disabled={!newComment[post.id]?.trim()}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        ))
      )}

      {!loading && posts.length === 0 && (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary">
              Ingen innlegg ennå. Vær den første til å dele noe!
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MobileFeed;
